import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { Vacation } from "@victor/api-client";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";
import { NetworkBadge } from "../today/NetworkBadge";

/**
 * Phase D · Polish app agent : page « Mes 7 derniers jours ».
 *
 * Liste compacte des vacations passées (et à venir) sur ±7 jours, groupées
 * par jour. Permet à l'agent de revoir sa semaine et de cliquer sur une
 * journée pour rebondir sur TodayPage (qui sert déjà les pointages /
 * main courante / rondes en lecture).
 */
export function HistoryPage() {
  const { user } = useAuth();
  const [windowDays, setWindowDays] = useState<7 | 14 | 30>(7);

  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - windowDays);
  const to = new Date(today);
  to.setDate(to.getDate() + 1); // inclure aujourd'hui

  const fromIso = from.toISOString().slice(0, 10);
  const toIso = to.toISOString().slice(0, 10);

  const q = useQuery({
    queryKey: ["me-history", fromIso, toIso],
    queryFn: () => api.me.vacations({ from: fromIso, to: toIso }),
  });

  const grouped = useMemo(() => groupByDay(q.data ?? []), [q.data]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <header className="sticky top-0 z-10 border-b border-navy-100 bg-white/90 px-5 py-3 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
              {user?.prenom ?? ""}
            </p>
            <h1 className="font-display text-xl font-black tracking-tight text-navy-800">
              Mon historique
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <NetworkBadge />
            <Link
              to="/"
              className="rounded-full bg-navy-50 px-3 py-1.5 text-xs font-semibold text-navy-700 transition-colors hover:bg-navy-100"
            >
              Aujourd'hui
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 py-4">
        <div className="mb-4 inline-flex rounded-full bg-navy-50 p-1 ring-1 ring-navy-100">
          {([7, 14, 30] as const).map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setWindowDays(d)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                windowDays === d
                  ? "bg-white text-navy-800 shadow-sm ring-1 ring-navy-100"
                  : "text-navy-600"
              }`}
            >
              {d} j
            </button>
          ))}
        </div>

        {q.isLoading ? (
          <p className="rounded-2xl bg-white p-6 text-center text-sm text-navy-500 ring-1 ring-navy-100">
            Chargement…
          </p>
        ) : null}

        {q.error ? (
          <p className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
            Impossible de charger l'historique : {(q.error as Error).message}
          </p>
        ) : null}

        {q.data && q.data.length === 0 ? (
          <p className="rounded-2xl bg-white p-6 text-center text-sm text-navy-500 ring-1 ring-navy-100">
            Aucune vacation sur la période.
          </p>
        ) : null}

        <ul className="space-y-3">
          {grouped.map(({ day, vacations }) => (
            <li
              key={day}
              className="rounded-2xl bg-white ring-1 ring-navy-100"
            >
              <Link
                to={`/?date=${day}`}
                className="block px-4 py-3 hover:bg-navy-50/40 transition-colors"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-accent-500">
                    {formatDay(day)}
                  </p>
                  <span className="text-[11px] text-navy-400">
                    {vacations.length} vacation{vacations.length > 1 ? "s" : ""}
                  </span>
                </div>
                <ul className="mt-2 space-y-1.5">
                  {vacations.map((v) => (
                    <li
                      key={v.id}
                      className="flex items-center justify-between gap-2 text-sm"
                    >
                      <span className="font-bold text-navy-800">
                        {formatTime(v.debut)} → {formatTime(v.fin)}
                      </span>
                      <StatusChip statut={v.statut} />
                    </li>
                  ))}
                </ul>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const STATUT_TONE: Record<string, string> = {
  pourvoir: "bg-navy-100 text-navy-700",
  affectee: "bg-accent-50 text-accent-700",
  terminee: "bg-emerald-50 text-emerald-700",
  annulee: "bg-red-50 text-red-700",
};

function StatusChip({ statut }: { statut: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
        STATUT_TONE[statut] ?? STATUT_TONE.affectee
      }`}
    >
      {statut}
    </span>
  );
}

function groupByDay(
  vacations: Vacation[],
): Array<{ day: string; vacations: Vacation[] }> {
  const m = new Map<string, Vacation[]>();
  for (const v of vacations) {
    const day = v.debut.slice(0, 10);
    const arr = m.get(day) ?? [];
    arr.push(v);
    m.set(day, arr);
  }
  return [...m.entries()]
    .sort(([a], [b]) => (a < b ? 1 : -1)) // antichronologique
    .map(([day, vs]) => ({
      day,
      vacations: vs.sort((a, b) => (a.debut < b.debut ? -1 : 1)),
    }));
}

function formatDay(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}
